import React, { useEffect, useMemo, useState } from "react";
import {
  Search, Lock, ChevronDown, Check, Sparkles, Banana, Flower, PenTool, Type, Zap, Wand2, Mountain, Rocket,
  Film, Clapperboard, Waves, Globe, Maximize2, Mic, User, Move, Shuffle, AudioLines, Languages,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { sampleFor } from "@/lib/samples";
import { brand } from "@/lib/api";
import { useRate } from "@/context/RateContext";

const ICONS = { sparkles: Sparkles, banana: Banana, flower: Flower, pen: PenTool, type: Type, zap: Zap, wand: Wand2,
  mountain: Mountain, rocket: Rocket, film: Film, clapper: Clapperboard, waves: Waves, globe: Globe, maximize: Maximize2,
  mic: Mic, user: User, move: Move, shuffle: Shuffle, audio: AudioLines, languages: Languages };

export const ModelIcon = ({ icon, className = "w-4 h-4" }) => {
  const I = ICONS[icon] || Sparkles;
  return <I className={className} />;
};

const lockReason = (m, configured) => {
  if (!configured) return "Coming Soon";
  if (m.section === "audio") return "Audio models are coming soon — unlocks automatically";
  return "Coming soon — unlocks automatically";
};

const both = (usd, rate) => `₹${(usd * rate).toFixed(2)} · $${Number(usd).toFixed(3)}`;
const priceHint = (m, rate) => {
  const p = m.pricing || {};
  if (p.basis === "per_image") return p.usd_per_image ? `from ${both(p.usd_per_image, rate)}/img` : "";
  const map = p.usd_per_second_by_resolution;
  if (map) return `from ${both(Math.min(...Object.values(map).map(Number)), rate)}/s`;
  if (p.usd_per_second) return `${both(p.usd_per_second, rate)}/s`;
  return "";
};

export default function ModelPicker({ models, value, onChange, configured, groups, testid = "model-picker", compact = false, changeButton = false }) {
  const { rate } = useRate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hover, setHover] = useState(null);
  useEffect(() => { if (!open) setQ(""); }, [open]);
  const active = models.find((m) => m.id === value);

  const grouped = useMemo(() => {
    const needle = q.toLowerCase();
    const hit = (m) => !needle || `${m.name} ${m.description}`.toLowerCase().includes(needle);
    return groups.map((g) => ({ ...g, items: models.filter((m) => g.filter(m) && hit(m)) })).filter((g) => g.items.length);
  }, [q, models, groups]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {changeButton ? (
          <button data-testid={`${testid}-trigger`} className="ax-chip text-white font-medium">Change</button>
        ) : (
        <button data-testid={`${testid}-trigger`}
          className={`ax-chip ${compact ? "" : "min-w-[190px]"} justify-between`}>
          <span className="flex items-center gap-2 min-w-0">
            <span className="ax-model-icon"><ModelIcon icon={active?.icon} className="w-3.5 h-3.5" /></span>
            <span className="truncate font-medium text-white">{active?.name || "Choose model"}</span>
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
        </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" side="top" sideOffset={8}
        className="w-[660px] p-0 bg-[#121214] border-zinc-800 text-zinc-100 rounded-xl shadow-2xl flex" data-testid={`${testid}-popover`}>
        <div className="w-[380px] shrink-0 border-r border-zinc-800">
        <div className="p-2.5 border-b border-zinc-800">
          <div className="flex items-center gap-2 bg-[#0A0A0A] border border-zinc-800 rounded-lg px-2.5 h-9 focus-within:border-[#5FC0C8]/60">
            <Search className="w-3.5 h-3.5 text-zinc-500" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search models…"
              data-testid={`${testid}-search`} className="bg-transparent outline-none text-sm w-full placeholder:text-zinc-600" />
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto p-1.5">
          {grouped.length === 0 && <div className="text-center text-zinc-500 text-sm py-8">No models match “{q}”</div>}
          {grouped.map((g) => (
            <div key={g.label} className="mb-1.5">
              <div className="px-2 pt-2 pb-1 text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">{g.label}</div>
              {g.items.map((m) => (
                <button key={`${g.label}-${m.id}`} disabled={!m.available}
                  onClick={() => { onChange(m); setOpen(false); }}
                  onMouseEnter={() => setHover(m)}
                  title={m.available ? m.description : lockReason(m, configured)}
                  data-testid={`model-option-${m.id}`}
                  className={`w-full text-left flex items-start gap-2.5 p-2 rounded-lg transition-colors ${m.available ? "hover:bg-zinc-800/70" : "opacity-50 cursor-not-allowed"} ${m.id === value ? "bg-zinc-800/60" : ""}`}>
                  <span className="ax-model-icon mt-0.5"><ModelIcon icon={m.icon} className="w-3.5 h-3.5" /></span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-white truncate">{brand(m.name)}</span>
                      {!m.available && <Lock className="w-3 h-3 text-zinc-400" data-testid={`model-lock-${m.id}`} />}
                      {m.id === value && <Check className="w-3.5 h-3.5 text-[#7AF25F]" />}
                    </span>
                    <span className="block text-xs text-zinc-500 line-clamp-1">{m.available ? m.description : lockReason(m, configured)}</span>
                  </span>
                  {m.available && <span className="text-[10px] font-mono text-zinc-500 whitespace-nowrap mt-0.5">{priceHint(m, rate)}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
        </div>
        <Preview m={hover || active} />
      </PopoverContent>
    </Popover>
  );
}

const Preview = ({ m }) => {
  if (!m) return <div className="flex-1" />;
  const caps = m.capabilities || {};
  const moving = m.section !== "image";
  const facts = [
    caps.resolutions?.length && `Resolution: ${caps.resolutions.join(" · ")}`,
    caps.duration_seconds && `Duration: ${caps.duration_seconds.min}–${caps.duration_seconds.max} s`,
    caps.max_images > 1 && `Up to ${caps.max_images} images per run`,
    caps.image_edit && "Edits & reference images",
    caps.image_to_video && "Image → video",
    caps.reference_images && "Character references",
    caps.audio && "Native sound",
  ].filter(Boolean);
  return (
    <div className="flex-1 p-3 flex flex-col gap-3 min-w-0" data-testid="model-preview">
      <div className="relative aspect-video rounded-lg overflow-hidden bg-black border border-zinc-800">
        <img key={m.id} src={sampleFor(m)} alt="" className={`w-full h-full object-cover ${moving ? "ax-kenburns" : "ax-fade-up"}`} />
        <span className="absolute top-2 left-2 ax-badge bg-black/60">{moving ? "Sample clip" : "Sample"}</span>
        {!m.available && <span className="absolute inset-0 bg-black/60 flex items-center justify-center text-xs text-zinc-300"><Lock className="w-3.5 h-3.5 mr-1" />Locked</span>}
      </div>
      <div>
        <div className="text-sm font-semibold text-white" data-testid="model-preview-name">{brand(m.name)}</div>
        <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{m.description}</p>
      </div>
      <ul className="space-y-1">{facts.map((f) => <li key={f} className="text-[11px] text-zinc-500 flex gap-1.5"><span className="text-[#7AF25F]">•</span>{f}</li>)}</ul>
    </div>
  );
};
